import {
    ArrowPathRoundedSquareIcon,
    PencilIcon,
    PlayIcon,
    TrashIcon,
} from '@heroicons/react/24/outline';
import { ColumnDef } from '@tanstack/react-table';
import { isEmpty } from 'radash';
import { useState } from 'react';

import { IconButton } from '@/components/core/IconButton';
import { StatusColumn } from '@/components/shared';
import ConfirmationModal from '@/dialogs/confirmation-modals/ConfirmationModal.tsx';
import { PUBLICATION_MODE } from '@/dialogs/publish-to-recipients/types';
import { useDialogHost } from '@/hooks/useDialogHost';
import { useOrganization } from '@/hooks/useOrganization';
import { useToast } from '@/hooks/useToast';
import { useTranslation } from '@/hooks/useTranslation';
import { useUser } from '@/hooks/useUser';
import { getInjector, getRootScope } from '@/services/angularjs-bridge';
import { formatDateToMedium } from '@/services/utils';

import {
    fetchSelection,
    useDeleteJob,
    useRunJob,
    useToggleJobExecutionStatus,
} from './executed.publication.queries';
import { JobDataType } from './executed.publication.types';
import {
    DestinationSource,
    isScheduledJobAllowed,
    isScheduledJobBusy,
} from './scheduled.publication';

type UseScheduledJobColumnsArgs = {
    channels?: readonly DestinationSource[];
};

const useScheduledJobColumns = ({
    channels,
}: UseScheduledJobColumnsArgs = {}): ColumnDef<JobDataType>[] => {
    const { t } = useTranslation();
    const { toast } = useToast();
    const { organization } = useOrganization();
    const { user } = useUser();
    const { openPublishToRecipients } = useDialogHost();
    const runJob = useRunJob();
    const toggleExecutionStatus = useToggleJobExecutionStatus();
    const deleteJob = useDeleteJob();

    const [pendingRunJobId, setPendingRunJobId] = useState<number | null>(null);
    const [pendingDeleteJobId, setPendingDeleteJobId] = useState<number | null>(null);

    const canManageJob = (job: JobDataType, isVerificationRequired: boolean) =>
        isScheduledJobAllowed(
            job,
            isVerificationRequired,
            user,
            organization as { verified?: unknown } | undefined,
            channels,
        );

    const handleEdit = async (job: JobDataType) => {
        if (!job.selectionId) {
            return;
        }

        try {
            const selection = await fetchSelection(job.selectionId);
            if (isEmpty(selection)) {
                return;
            }

            openPublishToRecipients({
                itemSelection: selection,
                mode: PUBLICATION_MODE.PUBLISH,
                publicationData: { job },
            });
        } catch {
            toast.error('PUBLICATION_JOB.PUBLICATION_STARTED_FAILED');
        }
    };

    return [
        {
            accessorKey: 'name',
            header: t('ITEM_LIBRARY_PUBLISH.attribute.name').toUpperCase(),
            size: 140,
            minSize: 100,
            enableResizing: true,
        },
        {
            accessorKey: 'publicationType',
            header: t('PUBLICATIONS.PUBLICATION_TYPE').toUpperCase(),
            size: 180,
            minSize: 170,
            enableResizing: true,
            cell: ({ row }) => (
                <span>
                    {t(`PUBLICATIONS.PUBLICATION_TYPE.${row.original.publicationType}`)}
                </span>
            ),
        },
        {
            accessorKey: 'status',
            header: t('PUBLICATIONS.EXECUTION_STATUS').toUpperCase(),
            size: 180,
            minSize: 170,
            enableResizing: true,
            cell: ({ row }) => {
                const executionStatus = row.original.status?.trim();
                if (!executionStatus) {
                    return null;
                }

                return (
                    <StatusColumn
                        status={executionStatus}
                        message={row.original.message}
                    />
                );
            },
        },
        {
            id: 'scheduleStatus',
            accessorKey: 'schedule.status',
            header: t('PUBLICATIONS.STATUS').toUpperCase(),
            size: 110,
            minSize: 90,
            enableResizing: true,
            cell: ({ row }) => (
                <StatusColumn
                    status={row.original.schedule?.status ?? ''}
                    message={row.original.message}
                />
            ),
        },
        {
            accessorKey: 'user',
            header: t('PUBLICATIONS.USER').toUpperCase(),
            size: 180,
            minSize: 140,
            enableResizing: true,
            cell: ({ row }) => {
                const injector = getInjector();
                const $rootScope = getRootScope(injector);
                return <label>{$rootScope.getUserName(row.original.user)}</label>;
            },
        },
        {
            accessorKey: 'destinationName',
            header: t('PUBLICATIONS.DESTINATION').toUpperCase(),
            size: 180,
            minSize: 140,
            enableResizing: true,
        },
        {
            id: 'nextRunDateTime',
            accessorKey: 'schedule.nextRunDateTime',
            header: t('ITEM_LIBRARY_PUBLISH.attribute.nextRunDateTime').toUpperCase(),
            size: 180,
            minSize: 140,
            enableResizing: true,
            cell: ({ row }) => {
                const nextRunDateTime = row.original.schedule?.nextRunDateTime;
                return nextRunDateTime ? (
                    <div className="tw-whitespace-nowrap">
                        {formatDateToMedium(new Date(nextRunDateTime))}
                    </div>
                ) : null;
            },
        },
        {
            id: 'actions',
            accessorKey: 'actions',
            header: t('ACTIONS').toUpperCase(),
            meta: {
                pinned: true,
            },
            size: 140,
            enableResizing: false,
            enableSorting: false,
            cell: ({ row }) => {
                const job = row.original;
                if (!canManageJob(job, false)) {
                    return null;
                }

                return (
                    <div
                        id={`row-${job.id}-actions`}
                        className="tw-flex tw-flex-row-reverse tw-gap-1 tw-z-999"
                    >
                        <IconButton
                            icon={TrashIcon}
                            size="md"
                            title={t('PUBLICATION_JOB.DELETE')}
                            data-testid={`scheduled-job-delete-${job.id}`}
                            onClick={(event) => {
                                event.stopPropagation();
                                setPendingDeleteJobId(job.id);
                            }}
                        />
                        {canManageJob(job, true) && (
                            <IconButton
                                icon={PlayIcon}
                                size="md"
                                title={t('PUBLICATION_JOB.RUN')}
                                disabled={isScheduledJobBusy(job)}
                                data-testid={`scheduled-job-run-${job.id}`}
                                onClick={(event) => {
                                    event.stopPropagation();
                                    setPendingRunJobId(job.id);
                                }}
                            />
                        )}
                        <IconButton
                            icon={ArrowPathRoundedSquareIcon}
                            size="md"
                            title={t('JOB_SCHEDULE.TOGGLE_EXECUTION_STATUS')}
                            data-testid={`scheduled-job-toggle-${job.id}`}
                            onClick={(event) => {
                                event.stopPropagation();
                                toggleExecutionStatus.mutate(job.id);
                            }}
                        />
                        <IconButton
                            icon={PencilIcon}
                            size="md"
                            title={t('JOB_SCHEDULE.EDIT_JOB')}
                            data-testid={`scheduled-job-edit-${job.id}`}
                            onClick={(event) => {
                                event.stopPropagation();
                                void handleEdit(job);
                            }}
                        />
                        <ConfirmationModal
                            title="PUBLICATION_JOB.CONFIRM_RUN"
                            subTitle="PUBLICATION_JOB.CONFIRM_RUN_MESSAGE"
                            discardBtnText="PUBLICATION_JOB.RUN"
                            closeBtnText="CANCEL"
                            btnVariant="default"
                            show={pendingRunJobId === job.id}
                            onSubmit={() => {
                                runJob.mutate(job.id);
                                setPendingRunJobId(null);
                            }}
                            onClose={() => setPendingRunJobId(null)}
                        />
                        <ConfirmationModal
                            title="PUBLICATION_JOB.CONFIRM_DELETE"
                            subTitle="PUBLICATION_JOB.CONFIRM_DELETE_MESSAGE"
                            discardBtnText="PUBLICATION_JOB.DELETE"
                            closeBtnText="CANCEL"
                            btnVariant="destructive"
                            show={pendingDeleteJobId === job.id}
                            onSubmit={() => {
                                deleteJob.mutate(job.id);
                                setPendingDeleteJobId(null);
                            }}
                            onClose={() => setPendingDeleteJobId(null)}
                        />
                    </div>
                );
            },
        },
    ];
};

export { useScheduledJobColumns };
